import { useEffect, useMemo, useRef, useState } from 'react';
import type { ParsedMessage } from '../lib/parser';
import { fold } from '../lib/search';

/**
 * Finding something inside the conversation you are already reading.
 *
 * It searches the messages the pane has in hand rather than the archive:
 * opening a chat decrypts all of it, so the work is a string compare over
 * memory and the answer is instant. Whole-archive search is a different
 * question with a different cost, and it does not belong behind this icon.
 */
export function ChatSearch({ messages, onJump }: {
  messages: ParsedMessage[] | null;
  onJump: (seq: number) => void;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [at, setAt] = useState(0);
  const field = useRef<HTMLInputElement>(null);

  const hits = useMemo(() => {
    const needle = fold(q.trim());
    if (needle.length < 2 || !messages) return [];
    return messages.filter((m) => m.body && fold(m.body).includes(needle)).map((m) => m.seq);
  }, [q, messages]);

  // Land on the first hit as it is typed, the way a browser's own find does —
  // it turns a count into an answer without a second gesture.
  useEffect(() => {
    setAt(0);
    if (hits.length) onJump(hits[0]!);
  }, [hits]);

  // A different chat is a different question.
  useEffect(() => { setQ(''); setOpen(false); }, [messages]);

  const go = (d: number) => {
    if (!hits.length) return;
    const next = (at + d + hits.length) % hits.length;
    setAt(next);
    onJump(hits[next]!);
  };

  if (!open) {
    return (
      <button className="insearch-open" title="Search in this chat"
              aria-label="Search in this chat" onClick={() => {
                setOpen(true);
                // The field does not exist until this render commits.
                setTimeout(() => field.current?.focus(), 0);
              }}>
        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <circle cx="11" cy="11" r="6.5" /><path d="M16 16l4.5 4.5" />
        </svg>
      </button>
    );
  }

  return (
    <div className="insearch">
      <input ref={field} type="search" value={q} placeholder="Search in this chat"
             autoComplete="off" onChange={(e) => setQ(e.target.value)}
             onKeyDown={(e) => {
               if (e.key === 'Enter') { e.preventDefault(); go(e.shiftKey ? -1 : 1); }
               if (e.key === 'Escape') { setQ(''); setOpen(false); }
             }} />
      <span className="count">
        {q.trim().length < 2 ? '' : hits.length ? `${at + 1}/${hits.length}` : '0'}
      </span>
      <button onClick={() => go(-1)} disabled={!hits.length} aria-label="Previous match">↑</button>
      <button onClick={() => go(1)} disabled={!hits.length} aria-label="Next match">↓</button>
      <button onClick={() => { setQ(''); setOpen(false); }} aria-label="Close search">×</button>
    </div>
  );
}
